import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import Dialog from "../Dialog";
import Button from "../Button";
import TextField from "../TextField";
import { IGNITENEXT_ADMIN_EMAIL } from "../../Utils/constants";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseEmailList = (value) =>
  String(value || "")
    .split(/[,;\n]+/)
    .map((email) => email.trim())
    .filter(Boolean);

const validateEmailList = (emails, fieldLabel) => {
  const invalid = emails.filter((email) => !EMAIL_PATTERN.test(email));
  if (invalid.length === 0) return "";
  return `${fieldLabel} contains invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`;
};

const IdeaClarityDialog = ({
  isOpen,
  onClose,
  onSubmit,
  recipientEmail = "",
  recipientName = "",
  ideaTitle = "",
  initialBody = "",
  defaultCc = "",
  saving = false,
}) => {
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setCc(defaultCc?.trim() || "");
      setSubject(
        ideaTitle?.trim()
          ? `Clarity request: ${ideaTitle.trim()}`
          : "Clarity request for your idea"
      );
      setBody(initialBody || "");
      setErrors({});
    }
  }, [isOpen, initialBody, ideaTitle, defaultCc]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = () => {
    const nextErrors = {};
    const trimmedTo = recipientEmail.trim();
    const trimmedSubject = subject.trim();
    const trimmedBody = body.trim();
    const ccList = parseEmailList(cc);

    if (!trimmedTo) {
      nextErrors.to = "Student email is unavailable. Cannot send clarity request.";
    } else if (!EMAIL_PATTERN.test(trimmedTo)) {
      nextErrors.to = "Student email address is invalid.";
    }

    const ccError = validateEmailList(ccList, "CC");
    if (ccError) nextErrors.cc = ccError;

    if (!trimmedSubject) {
      nextErrors.subject = "Subject is required.";
    }

    if (!trimmedBody) {
      nextErrors.body = "Please describe what the student should clarify.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    // Always BCC the IgniteNext admin only — no BCC field is shown to the mentor.
    onSubmit({
      to: trimmedTo,
      cc: ccList,
      bcc: [IGNITENEXT_ADMIN_EMAIL],
      subject: trimmedSubject,
      body: trimmedBody,
    });
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Request clarity"
      description="Email the student about what needs more detail or correction."
      maxWidth="max-w-2xl"
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-lg bg-amber-500 shadow-sm">
            <Icon icon="lucide:mail" className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-amber-700">Sending to student</p>
            <p className="text-sm font-semibold text-slate-900 truncate">
              {recipientName ? `${recipientName} · ` : ""}
              {recipientEmail || "No student email available"}
            </p>
          </div>
        </div>
        {errors.to && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            <Icon icon="lucide:alert-circle" className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{errors.to}</span>
          </div>
        )}

        <TextField
          label="CC"
          name="cc"
          value={cc}
          onChange={(e) => {
            setCc(e.target.value);
            if (errors.cc) setErrors((prev) => ({ ...prev, cc: "" }));
          }}
          placeholder="manager@example.com, teammate@example.com"
          helperText="Separate multiple emails with commas (optional)"
          error={errors.cc}
          disabled={saving}
        />

        <TextField
          label="Subject"
          name="subject"
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            if (errors.subject) setErrors((prev) => ({ ...prev, subject: "" }));
          }}
          placeholder="Clarity request for your idea"
          required
          error={errors.subject}
          disabled={saving}
        />

        <TextField
          label="Message"
          name="body"
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            if (errors.body) setErrors((prev) => ({ ...prev, body: "" }));
          }}
          placeholder="e.g. Please expand your problem statement and add a clearer link to your demo video."
          multiline
          rows={7}
          required
          error={errors.body}
          disabled={saving}
        />

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-200">
          <Button
            variant="secondary"
            size="medium"
            onClick={handleClose}
            disabled={saving}
            className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={saving || !recipientEmail?.trim()}
            startIcon={
              saving ? (
                <Icon icon="svg-spinners:ring-resize" className="h-4 w-4" />
              ) : (
                <Icon icon="lucide:send" className="h-4 w-4" />
              )
            }
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {saving ? "Sending..." : initialBody?.trim() ? "Update request" : "Send request"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default IdeaClarityDialog;
